// Warning collector for normalization-time semicolon diagnostics.
// Example warning code: "missing-semicolon" with source line number.
import type { LuisCompileWarning } from '../types'
import { splitLines } from './lines'
import { isStandaloneVarCall } from './var-call'
import { hasInlineVarMissingSemicolon } from './semicolons'
import { findDeclarationColonIndex, startsCustomPropertyDeclarationAt, startsPropertyDeclarationAt } from './declarations'
import { isMultilineDeclarationContinuationStart } from './warnings-multiline'

// Detect likely missing semicolon lines and report them as soft warnings.
export function collectMissingSemicolonWarnings(source: string): LuisCompileWarning[] {
  const warnings: LuisCompileWarning[] = []
  const lines = splitLines(source)

  // Scan every source line and report declaration-like rows without `;`.
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const trimmed = lines[lineIndex].trim()
    // Skip blank lines to avoid noise in diagnostics.
    if (!trimmed) {
      continue
    } else {
      // Continue warning checks for non-empty lines.
    }

    // Skip block openers/closers and loop headers.
    if (isNonTerminatedLineAllowed(trimmed)) {
      continue
    } else {
      // Continue semicolon-specific checks.
    }

    // Skip warning for multi-line variable starts like `--x: [` or `--x: {`.
    if (isMultilineVariableStart(trimmed)) {
      continue
    } else {
      // Continue inline semicolon warning checks.
    }

    // Warn when inline `var(--x)` is not terminated before next declaration.
    if (hasInlineVarMissingSemicolon(trimmed)) {
      const selectorContext = findNearestSelectorContext(lines, lineIndex)
      warnings.push({
        code: 'missing-semicolon',
        line: lineIndex + 1,
        message: selectorContext
          ? `Missing semicolon after var(...) statement before next declaration. (selector: ${selectorContext})`
          : 'Missing semicolon after var(...) statement before next declaration.',
      })
    } else {
      // Continue end-of-line semicolon checks.
    }

    // Report only declaration-like lines that miss a trailing semicolon.
    if (!trimmed.endsWith(';') && looksLikeSemicolonRequiredLine(trimmed)) {
      // Allow css-valid last declaration in a block before closing brace.
      if (isLastDeclarationBeforeBlockClose(lines, lineIndex)) {
        continue
      } else if (isMultilineDeclarationContinuationStart(lines, lineIndex)) {
        // Skip false positives for multi-line declaration values.
        continue
      } else {
        // Continue warning emission for truly unterminated declaration lines.
      }
      const selectorContext = findNearestSelectorContext(lines, lineIndex)
      warnings.push({
        code: 'missing-semicolon',
        line: lineIndex + 1,
        message: selectorContext
          ? `Missing semicolon at end of line. (selector: ${selectorContext})`
          : 'Missing semicolon at end of line.',
      })
    } else {
      // Keep warnings unchanged when line ends correctly.
    }
  }

  // Return collected warning list.
  return warnings
}

// Allow known line forms that are valid without `;` terminator.
function isNonTerminatedLineAllowed(trimmed: string): boolean {
  // Keep braces and block opener lines out of warning set.
  if (trimmed === '{' || trimmed === '}' || trimmed.endsWith('{') || trimmed.endsWith('}')) {
    return true
  } else if (trimmed.startsWith('@for') || trimmed.startsWith('for(') || trimmed.startsWith('for (')) {
    // Keep loop headers without trailing semicolon valid.
    return true
  } else {
    // Return false for all other line kinds.
    return false
  }
}

// Detect rows where semicolon termination is expected by project rules.
function looksLikeSemicolonRequiredLine(trimmed: string): boolean {
  // Detect custom-property declaration rows like `--x: ...`.
  if (startsCustomPropertyDeclarationAt(trimmed, 0)) {
    return true
  } else if (startsPropertyDeclarationAt(trimmed, 0)) {
    // Detect direct property declaration rows like `color: ...`.
    return true
  } else if (isStandaloneVarCall(trimmed)) {
    // Detect standalone `var(--x)` or `var(--x.key)` statement calls.
    return true
  } else if (trimmed.includes('&var(')) {
    // Detect selector composition statements with `&var(--rule)`.
    return true
  } else {
    // Return false when line does not require semicolon by rule.
    return false
  }
}

// Detect first line of multi-line custom-property declarations.
function isMultilineVariableStart(trimmed: string): boolean {
  // Keep false when line does not start with custom-property declaration.
  if (!startsCustomPropertyDeclarationAt(trimmed, 0)) {
    return false
  } else if (trimmed.endsWith('[') || trimmed.endsWith('{')) {
    // Keep open bracket/object starts out of one-line semicolon warnings.
    return true
  } else {
    // Continue multiline declaration checks.
  }

  const colonIndex = findDeclarationColonIndex(trimmed)
  // Reject when parser cannot find declaration colon safely.
  if (colonIndex === -1) {
    return false
  } else {
    // Return true for declarations with empty value after colon.
    return trimmed.slice(colonIndex + 1).trim().length === 0
  }
}

// Detect css-valid declaration lines that are immediately closed by `}`.
function isLastDeclarationBeforeBlockClose(lines: string[], lineIndex: number): boolean {
  const nextIndex = findNextMeaningfulLineIndex(lines, lineIndex + 1)
  // Return false when current line has no following non-empty line.
  if (nextIndex === -1) {
    return false
  } else {
    // Continue closing-brace line validation.
  }

  const nextTrimmed = lines[nextIndex].trim()
  // Return true for `}`, `};`, `},` and similar closing-brace starts.
  return nextTrimmed.startsWith('}')
}

// Find next non-empty source line index from a starting cursor.
function findNextMeaningfulLineIndex(lines: string[], fromIndex: number): number {
  // Scan forward and return first non-empty trimmed line index.
  for (let index = fromIndex; index < lines.length; index += 1) {
    if (lines[index].trim().length > 0) {
      return index
    } else {
      // Continue scan while line is empty/whitespace only.
    }
  }

  // Return -1 when no further non-empty line exists.
  return -1
}

// Find nearest selector header above current line for warning context.
function findNearestSelectorContext(lines: string[], fromIndex: number): string | null {
  // Scan upward until top for selector-like block opener lines.
  for (let index = fromIndex; index >= 0; index -= 1) {
    const trimmed = lines[index].trim()
    if (!trimmed) {
      continue
    } else if (trimmed.startsWith('@')) {
      // Skip at-rule lines for selector-context detection.
      continue
    } else {
      // Continue selector header checks for non-empty lines.
    }

    const braceIndex = trimmed.indexOf('{')
    if (braceIndex <= 0) {
      continue
    } else {
      // Continue selector extraction from block opener line.
    }

    const candidate = trimmed.slice(0, braceIndex).trim()
    if (!candidate || candidate.includes(':') && !candidate.startsWith('.') && !candidate.startsWith('#')) {
      continue
    } else {
      // Return nearest selector-like candidate for warning message.
      return candidate
    }
  }

  // Return null when selector context cannot be inferred.
  return null
}
